import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const login = (username, password) =>
  api.post('/auth/login', { username, password });

export const register = (username, password, role) =>
  api.post('/auth/register', { username, password, role });

export const getUsers = () => api.get('/auth/users');

export const changeUserPassword = (id, password) =>
  api.patch(`/auth/users/${id}/password`, { password });

export const deleteUser = (id) => api.delete(`/auth/users/${id}`);

// Products
export const getProducts = () => api.get('/products');

export const getProduct = (id) => api.get(`/products/${id}`);

export const getProductByBarcode = (barcode) =>
  api.get(`/products/barcode/${barcode}`);

export const createProduct = (formData) =>
  api.post('/products', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const updateProduct = (id, formData) =>
  api.put(`/products/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const updateProductLocation = (id, location) =>
  api.patch(`/products/${id}/location`, { location });

export const updateProductPrice = (id, price) =>
  api.patch(`/products/${id}/price`, { price });

export const deleteProduct = (id) => api.delete(`/products/${id}`);

export const getAvailableProductsForShipping = () => api.get('/products/available/for-shipping');

// Product Photos
export const addProductPhotos = (productId, formData) =>
  api.post(`/products/${productId}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const deleteProductPhoto = (productId, photoId) =>
  api.delete(`/products/${productId}/photos/${photoId}`);

export const reorderProductPhotos = (productId, photoIds) =>
  api.put(`/products/${productId}/photos/reorder`, { photoIds });

// Shipping
export const getNextBoxNumber = () => api.get('/shipping/next-box-number');

export const getShippingBoxes = () => api.get('/shipping');

export const getShippingBox = (id) => api.get(`/shipping/${id}`);

export const createShippingBox = (data) => api.post('/shipping', data);

export const updateShippingBoxStatus = (id, status) =>
  api.patch(`/shipping/${id}/status`, { status });

export const updateShippingDate = (id, shipping_date) =>
  api.patch(`/shipping/${id}/shipping-date`, { shipping_date });

export const updateShippingCost = (id, shipping_cost) =>
  api.patch(`/shipping/${id}/shipping-cost`, { shipping_cost });

export const getShippingPDF = (id) =>
  api.get(`/shipping/${id}/pdf`, { responseType: 'blob' });

export const updateShippingBox = (id, data) =>
  api.patch(`/shipping/${id}`, data);

export const addShippingItem = (boxId, data) =>
  api.post(`/shipping/${boxId}/items`, data);

export const updateShippingItem = (boxId, itemId, quantity) =>
  api.patch(`/shipping/${boxId}/items/${itemId}`, { quantity });

export const removeShippingItem = (boxId, itemId) =>
  api.delete(`/shipping/${boxId}/items/${itemId}`);

// Inventory
export const getInventoryAdjustments = () => api.get('/inventory/adjustments');

export const getPendingAdjustments = () =>
  api.get('/inventory/adjustments/pending');

export const createAdjustment = (data) =>
  api.post('/inventory/adjustments', data);

export const approveRejectAdjustment = (id, status) =>
  api.patch(`/inventory/adjustments/${id}`, { status });

export const directInventoryAdjust = (productId, quantityChange, reason) =>
  api.post('/inventory/adjust', {
    product_id: productId,
    quantity_change: quantityChange,
    reason,
  });

// Invoices
export const getCustomers = () => api.get('/invoices/customers');

export const createCustomer = (data) => api.post('/invoices/customers', data);

export const updateCustomer = (id, data) => api.put(`/invoices/customers/${id}`, data);

export const getInvoices = () => api.get('/invoices');

export const getInvoice = (id) => api.get(`/invoices/${id}`);

export const createInvoice = (data) => api.post('/invoices', data);

export const addInvoiceItem = (invoiceId, data) =>
  api.post(`/invoices/${invoiceId}/items`, data);

export const updateInvoiceItemPrice = (invoiceId, itemId, unitPrice) =>
  api.patch(`/invoices/${invoiceId}/items/${itemId}/price`, {
    unit_price: unitPrice,
  });

export const updateInvoiceItemQuantity = (invoiceId, itemId, quantity) =>
  api.patch(`/invoices/${invoiceId}/items/${itemId}/quantity`, { quantity });

export const removeInvoiceItem = (invoiceId, itemId) =>
  api.delete(`/invoices/${invoiceId}/items/${itemId}`);

export const deleteInvoice = (id) => api.delete(`/invoices/${id}`);

export const getInvoicePDF = (id) =>
  api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });

// Payment Methods
export const getPaymentMethods = () => api.get('/invoices/payment-methods');

export const createPaymentMethod = (name) =>
  api.post('/invoices/payment-methods', { name });

// Invoice Payments
export const getInvoicePayments = (invoiceId) =>
  api.get(`/invoices/${invoiceId}/payments`);

export const addInvoicePayment = (invoiceId, data) =>
  api.post(`/invoices/${invoiceId}/payments`, data);

export const reopenInvoice = (id) => api.patch(`/invoices/${id}/reopen`);

// Vendors
export const getVendors = () => api.get('/vendors');

export const createVendor = (name) => api.post('/vendors', { name });

// Carriers
export const getCarriers = () => api.get('/carriers');

export const createCarrier = (name) => api.post('/carriers', { name });

// Box Sizes
export const getBoxSizes = () => api.get('/box-sizes');

export const createBoxSize = (name) => api.post('/box-sizes', { name });

// Events
export const getEvents = () => api.get('/events');

export const getEvent = (id) => api.get(`/events/${id}`);

export const createEvent = (data) => api.post('/events', data);

export const updateEventStatus = (id, status) =>
  api.patch(`/events/${id}/status`, { status });

export const getEventPackingLists = (eventId) =>
  api.get(`/events/${eventId}/packing-lists`);

export const createEventPackingList = (eventId, data) =>
  api.post(`/events/${eventId}/packing-lists`, data);

export const getPackingListPDF = (eventId, listId) =>
  api.get(`/events/${eventId}/packing-lists/${listId}/pdf`, {
    responseType: 'blob',
  });

export default api;
